/**
 * Flujo guiado de restauración de respaldo JSON (validación → revisión → confirmaciones).
 * No restaura hasta completar todas las etapas.
 */
import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeDialog } from "@/components/nativeUi";
import * as dataIo from "@/lib/dataIo";
import {
  formatBackupDateTime,
  formatFileSize,
  parseAndValidateBackupFile,
} from "@/lib/backupRestoreValidate";

const CONFIRM_WORD = "RESTAURAR";

/**
 * @param {{
 *   busy?: boolean,
 *   onBusyChange?: (busy: boolean) => void,
 *   onMessage?: (msg: { type: "ok"|"err", text: string } | null) => void,
 *   onCompleted?: () => void,
 *   onPreventiveBackupDone?: () => void,
 * }} props
 */
export function RestoreBackupWizard({
  busy = false,
  onBusyChange,
  onMessage,
  onCompleted,
  onPreventiveBackupDone,
}) {
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("review"); // review | confirm | restoring
  const [file, setFile] = useState(null);
  const [summary, setSummary] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [localError, setLocalError] = useState("");
  const [statusNote, setStatusNote] = useState("");

  const resetWizard = () => {
    setOpen(false);
    setStep("review");
    setFile(null);
    setSummary(null);
    setConfirmText("");
    setLocalError("");
    setStatusNote("");
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && step !== "restoring") resetWizard();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, step]);

  const beginPick = () => {
    if (busy) return;
    onMessage?.(null);
    setLocalError("");
    inputRef.current?.click();
  };

  const onFileChange = async (e) => {
    const picked = e.target.files?.[0];
    e.target.value = "";
    if (!picked) return;

    onBusyChange?.(true);
    onMessage?.(null);
    setLocalError("");
    try {
      const { file: validatedFile, summary: sum } = await parseAndValidateBackupFile(picked);
      setFile(validatedFile);
      setSummary(sum);
      setConfirmText("");
      setStep("review");
      setOpen(true);
    } catch (err) {
      const text = err?.message || "El archivo no es un respaldo válido de ICJEME.";
      onMessage?.({ type: "err", text });
      resetWizard();
    } finally {
      onBusyChange?.(false);
    }
  };

  const handleCancel = () => {
    if (step === "restoring") return;
    resetWizard();
  };

  const handleContinueToConfirm = () => {
    setLocalError("");
    setConfirmText("");
    setStep("confirm");
  };

  const canRestore = confirmText.trim() === CONFIRM_WORD;

  const handleRestore = async () => {
    if (!file || !canRestore || step === "restoring") return;
    setLocalError("");
    setStep("restoring");
    onBusyChange?.(true);
    onMessage?.(null);

    try {
      setStatusNote("Creando respaldo preventivo del estado actual…");
      try {
        await dataIo.downloadBackupJson();
        onPreventiveBackupDone?.();
      } catch (prevErr) {
        let text = dataIo.errorToUserMessage(prevErr);
        try {
          const fromApi = await dataIo.extractAxiosErrorMessage(prevErr);
          if (fromApi && String(fromApi).trim()) text = String(fromApi).trim();
        } catch {
          /* keep text */
        }
        throw new Error(
          `No se pudo crear el respaldo preventivo del estado actual. La restauración se canceló para no modificar datos. Detalle: ${text}`,
        );
      }

      setStatusNote("Restaurando el respaldo seleccionado…");
      const res = await dataIo.restoreBackupJson(file);
      const ins = res.inserted || {};
      const summaryText = [
        `células ${ins.cells ?? 0}`,
        `miembros ${ins.members ?? 0}`,
        `invitados ${ins.guests ?? 0}`,
        `asistencias ${ins.attendance ?? 0}`,
        `asist. reunión ${ins.cell_meeting_attendance ?? 0}`,
        `sesiones reunión ${ins.cell_meeting_sessions ?? 0}`,
        `discipulado ${ins.cell_discipleship_attendance ?? 0}`,
        `estad. rápida ${ins.cell_quick_weekly_stats ?? 0}`,
      ].join(", ");
      const warn =
        Array.isArray(res.warnings) && res.warnings.length ? ` Avisos: ${res.warnings.join(" ")}` : "";
      onMessage?.({
        type: "ok",
        text: `Restauración completada (${summaryText}). Se descargó un respaldo preventivo del estado anterior.${warn}`,
      });
      onCompleted?.();
      resetWizard();
    } catch (err) {
      let text = err?.message || dataIo.errorToUserMessage(err);
      try {
        const fromApi = await dataIo.extractAxiosErrorMessage(err);
        if (fromApi && String(fromApi).trim()) text = String(fromApi).trim();
      } catch {
        /* keep */
      }
      setLocalError(text);
      onMessage?.({ type: "err", text });
      setStep("confirm");
      setStatusNote("");
    } finally {
      onBusyChange?.(false);
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".json,application/json"
        className="hidden"
        onChange={(e) => {
          void onFileChange(e);
        }}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={busy}
        onClick={beginPick}
        className="border-amber-300 text-amber-900 hover:bg-amber-50"
      >
        <Upload className="w-4 h-4 mr-1" />
        Restaurar respaldo…
      </Button>

      <NativeDialog
        open={open}
        onClose={step === "restoring" ? () => {} : handleCancel}
        className="w-full max-w-xl p-6"
      >
        <h2 className="text-xl font-bold text-gray-900 pr-8 mb-1">Restaurar respaldo</h2>
        <p className="text-sm text-gray-600 mb-4">
          {step === "review" && "Revise la información del archivo antes de continuar."}
          {step === "confirm" && "Confirmación final requerida para ejecutar la restauración."}
          {step === "restoring" && "Operación en curso. No cierre esta ventana."}
        </p>

        {summary && (step === "review" || step === "confirm" || step === "restoring") && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 mb-4 space-y-2 text-sm">
            <p>
              <span className="font-semibold text-gray-800">Archivo: </span>
              <span className="text-gray-700">{summary.fileName}</span>
              <span className="text-gray-500"> ({formatFileSize(summary.fileSizeBytes)})</span>
            </p>
            <p>
              <span className="font-semibold text-gray-800">Fecha y hora del respaldo: </span>
              <span className="text-gray-700">{formatBackupDateTime(summary.exportedAt)}</span>
            </p>
            {summary.schemaVersion ? (
              <p>
                <span className="font-semibold text-gray-800">Versión de formato: </span>
                <span className="text-gray-700">{summary.schemaVersion}</span>
              </p>
            ) : null}
            {summary.exportKind ? (
              <p>
                <span className="font-semibold text-gray-800">Tipo: </span>
                <span className="text-gray-700">
                  {summary.exportKind === "full" ? "Respaldo completo de iglesia" : summary.exportKind}
                </span>
              </p>
            ) : null}
            <div className="pt-2 border-t border-gray-200">
              <p className="font-semibold text-gray-800 mb-1">Contenido</p>
              <ul className="space-y-1">
                {summary.countRows.map((row) => (
                  <li key={row.key} className="flex justify-between gap-3 text-gray-700">
                    <span>{row.label}</span>
                    <span className="tabular-nums font-medium">{row.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {step === "review" && (
          <>
            <div className="rounded-lg border border-red-300 bg-red-50 p-4 mb-4">
              <div className="flex gap-2 items-start">
                <AlertTriangle className="w-5 h-5 text-red-700 shrink-0 mt-0.5" aria-hidden />
                <p className="text-sm font-semibold text-red-900 leading-snug">
                  ADVERTENCIA: RESTAURAR UN RESPALDO PUEDE REEMPLAZAR LA INFORMACIÓN ACTUAL DEL SISTEMA.
                  ESTA OPERACIÓN NO DEBE REALIZARSE SIN ESTAR SEGURO.
                </p>
              </div>
              <p className="text-xs text-red-800 mt-2 ml-7">
                Se sustituirán células, miembros, invitados, asistencias y datos relacionados. Los usuarios de
                inicio de sesión no se modifican.
              </p>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancelar
              </Button>
              <Button type="button" className="bg-amber-700 hover:bg-amber-800" onClick={handleContinueToConfirm}>
                Continuar
              </Button>
            </div>
          </>
        )}

        {(step === "confirm" || step === "restoring") && (
          <>
            <div className="space-y-2 mb-4">
              <Label htmlFor="restore-confirm-word">
                Para confirmar, escriba exactamente <span className="font-mono font-bold">{CONFIRM_WORD}</span>
              </Label>
              <Input
                id="restore-confirm-word"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={CONFIRM_WORD}
                disabled={step === "restoring"}
                autoComplete="off"
                spellCheck={false}
                className="font-mono"
              />
            </div>
            {statusNote ? <p className="text-sm text-blue-700 mb-3">{statusNote}</p> : null}
            {localError ? <p className="text-sm text-red-600 mb-3">{localError}</p> : null}
            <div className="flex flex-wrap justify-end gap-2">
              <Button type="button" variant="outline" onClick={handleCancel} disabled={step === "restoring"}>
                Cancelar
              </Button>
              <Button
                type="button"
                className="bg-red-700 hover:bg-red-800 disabled:opacity-50"
                disabled={!canRestore || step === "restoring"}
                onClick={() => {
                  void handleRestore();
                }}
              >
                {step === "restoring" ? "Restaurando…" : "Restaurar respaldo"}
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              Antes de modificar la base de datos se descargará automáticamente un respaldo preventivo del estado
              actual (mismo mecanismo que «Respaldo JSON»).
            </p>
          </>
        )}
      </NativeDialog>
    </>
  );
}
